import { useSubscription } from './useSubscription';
import { canTierUseFeature, getPlanConfig, requiredPlanForFeature, PlanFeature } from './useSubscriptionConfig';

// Features the tier table cannot unlock: no plan's gated_features contains
// them, so access comes from a per-subscription flag instead.
//
// EACH ONE MAPS TO ITS OWN FLAG. This used to be a Set, and canUse() returned
// `hasAiAddon` for every member of it — fine while there was exactly one
// member, and a silent grant the moment there were two: adding a feature here
// would have handed it to everyone who had bought the receptionist.
//
// ai_receptionist is an ADD-ON — a separate purchase on top of any plan.
// estimates is a ROLLOUT GATE — every paid plan includes it, this only says
// whether it has been switched on for that business yet. Same mechanism,
// different meaning; see 20260830100000_estimates_rollout_gate.sql.
interface FlagSource {
  ai_addon: boolean;
  estimates_enabled: boolean;
}

interface FlagRule {
  key: keyof FlagSource;
  /**
   * Whether bypass_subscription (comped billing) grants it on its own.
   *
   * TRUE for the receptionist: bypass means "treat as internal tier", and an
   * add-on is a paid thing, so comping the bill comps the add-on.
   *
   * FALSE for estimates, and that difference is the point. bypass answers
   * "has this business paid?"; the gate answers "has this shipped for them
   * yet?". On production ALEX HVAC LLC is comped with 111 jobs — below the
   * bypass it would be first in line for an unreleased feature because of how
   * it is billed. Mirrors the branch order in 20260830100000, where the gate
   * sits ABOVE the bypass check.
   */
  bypassGrants: boolean;
}

const FLAG_FEATURES: Partial<Record<PlanFeature, FlagRule>> = {
  ai_receptionist: { key: 'ai_addon', bypassGrants: true },
  estimates: { key: 'estimates_enabled', bypassGrants: false },
};

export function usePlanRestrictions() {
  const {
    subscribed, subscription_tier, bypass_subscription, is_trialing,
    ai_addon, estimates_enabled, loading,
  } = useSubscription();

  const effectiveTier = bypass_subscription ? 'internal' : subscription_tier;
  const plan = getPlanConfig(effectiveTier);
  // Mirror of user_has_plan_feature: bypass short-circuits, otherwise the
  // feature's own flag decides (already gated on active/trialing by the
  // backend).
  const flags: FlagSource = { ai_addon, estimates_enabled };
  const hasFlag = (feature: PlanFeature): boolean => {
    const rule = FLAG_FEATURES[feature];
    if (!rule) return false;
    if (rule.bypassGrants && bypass_subscription) return true;
    return flags[rule.key];
  };

  return {
    isLoading: loading,
    tier: effectiveTier,
    plan,
    isSubscribed: subscribed || bypass_subscription,
    isTrialing: is_trialing,
    isBypass: bypass_subscription,
    canUse: (feature: PlanFeature): boolean =>
      FLAG_FEATURES[feature] ? hasFlag(feature) : canTierUseFeature(effectiveTier, feature),
    isAddonFeature: (feature: PlanFeature): boolean => Boolean(FLAG_FEATURES[feature]),
    workersLimit: plan?.workers_included ?? 0,
    photoLimit: plan?.photo_limit ?? 0,
    requiredPlanForFeature: (feature: PlanFeature) => requiredPlanForFeature(feature),
  };
}
